import React, { useState } from 'react';
import { connect } from 'react-redux';
import IconButton from '@material-ui/core/IconButton';
import { Tooltip } from '@material-ui/core';
import ExportIcon from 'mdi-react/CloudDownloadIcon';
import LoadingIcon from 'mdi-react/DotsHorizontalIcon';
import { ExportToCsv } from 'export-to-csv';
import moment from 'moment';
import { _hasPermission } from '../../../shared/helpers/access';
import { _axiosCall } from '../../../shared/helpers/apicalls';
import {
	_capitalizeText,
	_formatNumber,
	_PickOrgName,
} from '../../../shared/helpers/utils';

const apiURL = process.env.API_ROOT + '/';

const _formatDate = (val) => {
	if (!val || val <= 0) return '';
	const m = moment(val);
	if (m.year() <= 1970) return '';
	return m.format('MM/DD/YYYY');
};

const _fetchOrganizations = async (buildingKeys) => {
	if (!buildingKeys || buildingKeys.length === 0) return {};

	let mongoQuery = [
		{
			$match: {
				key: { $in: buildingKeys },
			},
		},
		{
			"$lookup": {
				"from": "organizations",
				"localField": "key",
				"foreignField": "projects.key",
				"pipeline": [
					{
						"$match": {
							"meta.deleted": { "$exists": false }
						}
					},
					{
						"$project": {
							name: 1,
							key: 1,
							project_roles: 1,
							name_list: 1
						}
					}
				],
				"as": "organizations"
			}
		},
		{
			$project: {
				key: 1,
				organizations: 1
			}
		}
	];

	let axiosData = {
		url: apiURL + "pipeline/aggregate",
		method: "post",
		query: mongoQuery,
	};

	try {
		const res = await _axiosCall(axiosData);
		if (!res || !Array.isArray(res)) return {};

		// Build a map: { buildingKey: { developers: [...], architects: [...] } }
		const orgMap = {};
		res.forEach(building => {
			if (!building.organizations || !Array.isArray(building.organizations)) return;
			const bKey = building.key;
			if (!orgMap[bKey]) {
				orgMap[bKey] = { developers: [], architects: [] };
			}

			building.organizations.forEach(org => {
				const orgName = _PickOrgName(org) || org.name;
				if (!orgName) return;

				const roles = org.project_roles && org.project_roles[bKey];
				if (!Array.isArray(roles)) return;

				if (roles.some(r => r.toLowerCase().indexOf('developer') >= 0)) {
					orgMap[bKey].developers.push(_capitalizeText(orgName));
				}
				if (roles.some(r => r.toLowerCase().indexOf('architect') >= 0)) {
					orgMap[bKey].architects.push(_capitalizeText(orgName));
				}
			});
		});
		return orgMap;
	} catch (error) {
		console.log("error fetching organizations: " + error);
		return {};
	}
};

const PipelineExportButton = ({ user, data, page }) => {
	const [exporting, setExporting] = useState(false);

	if (!_hasPermission(user, 'pipeline-data-export')) return null;
	if (!data || data.length === 0) return null;

	const handleExport = async () => {
		setExporting(true);
		try {
			// Collect building keys for org lookup
			const buildingKeys = data
				.map(row => row.key)
				.filter(Boolean);

			const orgMap = await _fetchOrganizations(buildingKeys);

			const exportData = data.map(row => {
				const orgs = orgMap[row.key] || { developers: [], architects: [] };

				return {
					'Project Stage': row.pipeline_project_stage
						? _capitalizeText(row.pipeline_project_stage) : '',
					'Address': row.display_full_street_address
						? _capitalizeText(row.display_full_street_address) : '',
					'Name': row.name
						? _capitalizeText(row.name) : '',
					'Borough': row.address && row.address.borough
						? _capitalizeText(row.address.borough) : '',
					'Neighborhood': row.address && row.address.neighborhood && row.address.neighborhood[0]
						? _capitalizeText(row.address.neighborhood[0]) : '',
					'Zip Code': row.address && row.address.zip
						? row.address.zip : '',
					'Block': row.address && row.address.block
						? row.address.block : '',
					'Lot': row.address && row.address.lot
						? row.address.lot : '',
					'BBL': row.address && row.address.borough_code && row.address.block && row.address.lot
						? `${row.address.borough_code}/${row.address.block}/${row.address.lot}` : '',
					'Alt. Addresses': row.address && Array.isArray(row.address.alternate_addresses)
						? row.address.alternate_addresses.map(a => _capitalizeText(a)).join('; ') : '',
					'Developer': orgs.developers.length > 0
						? orgs.developers.join('; ') : '',
					'Architect': orgs.architects.length > 0
						? orgs.architects.join('; ') : '',
					'Floors': row.pipeline_building_stories_proposed > 0
						? row.pipeline_building_stories_proposed : '',
					'Units': row.pipeline_dwelling_units_proposed > 0
						? row.pipeline_dwelling_units_proposed : '',
					'Total Area (SF)': row.pipeline_total_construction_floor_area > 0
						? row.pipeline_total_construction_floor_area : '',
					'Occupancy Type': row.pipeline_occupancy_type
						? _capitalizeText(row.pipeline_occupancy_type) : '',
					'Project Components': Array.isArray(row.pipeline_project_components)
						? row.pipeline_project_components.map(v => _capitalizeText(v)).join(', ') : '',
					'Permit Filed': _formatDate(row.pipeline_filing_date || row.pipeline_pre_filing_date),
					'Permit Issued': _formatDate(row.pipeline_permit_issuance_job_start_date || row.pipeline_permit_issuance_issuance_date),
					'CO/TCO Issued': _formatDate(row.building_effective_start || row.pipeline_coo_issuance_date),
					'OP Submitted': _formatDate(row.offering_submitted_date),
					'Last Updated': row.meta && row.meta.ModificationTimestamp
						? moment(row.meta.ModificationTimestamp).format('MM/DD/YYYY') : '',
				};
			});

			const options = {
				filename: `Pipeline-Search-Export-${moment().format('YYYY-MM-DD')}`,
				useKeysAsHeaders: true,
			};
			const csvExporter = new ExportToCsv(options);
			csvExporter.generateCsv(exportData);
		} catch (error) {
			console.log("export error: " + error);
		}
		setExporting(false);
	};

	return (
		<Tooltip title={exporting ? 'Exporting...' : `Export Current List of Projects as CSV (Page ${(page || 0) + 1})`}>
			<IconButton
				onClick={handleExport}
				color="inherit"
				disabled={exporting}
			>
				{exporting ? <LoadingIcon /> : <ExportIcon />}
			</IconButton>
		</Tooltip>
	);
};

export default connect(state => ({
	user: state.user,
}))(PipelineExportButton);
